// src/pages/TransferRecord/components/TransferRecordTable.js
import React, {useState} from "react";
import { Table, Button, App, Modal, Tooltip } from "antd";
import { DeleteOutlined, DollarOutlined, EditOutlined, FileDoneOutlined, FileTextOutlined, PrinterOutlined, SolutionOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatToCompanyTimezone } from "../../../lib/functions";
import { deleteTransfer, fetchTransferRecord } from "../TransferRecordThunk";
import { useTxLock } from "../../../hooks/useTxLock";

const TransferRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.transferRecord
  );

  const dispatch = useDispatch();
  const { message } = App.useApp();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const navigate = useNavigate();

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const handleDelete = (t_id) => {
    Modal.confirm({
      title: "Delete Transfer?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await dispatch(deleteTransfer(t_id));

        if (res.meta.requestStatus === "fulfilled") {
          message.success("Transfer deleted successfully");
          dispatch(fetchTransferRecord({
            selectedFilterType: filters.filterType,
            selectedRecordType: filters.recordType,
            filters
          }));
        } else {
          message.error("Delete failed");
        }
      }
    });
  };


  // -----------------------
  // Columns for WITHOUT ITEM
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "vch_no" },
    { title: "Date", dataIndex: "date", render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-", },
    { title: "To Branch", dataIndex: "branch" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Transport Cost", dataIndex: "transport_cost" },
    { title: "Total", dataIndex: "total" },
    { title: "Status", dataIndex: "status" },
 !printMode && {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.date);

    return (
      <>
        {record.status === "Pending" && (
          <>
            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Edit"
              }
            >
              <Button
                type="link"
                disabled={txLocked}
                onClick={() => {
                  if (txLocked) return;

                  navigate(
                    `/inventory/transfer-entry/edit/${record.t_id}`
                  );
                }}
                icon={<EditOutlined />}
              />
            </Tooltip>

            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Delete"
              }
            >
              <Button
                type="link"
                danger
                disabled={txLocked}
                onClick={() => {
                  if (txLocked) return;

                  handleDelete(record.t_id);
                }}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </>
        )}

        <Button
          type="link"
          onClick={() =>
            navigate(
              `/inventory/quick-transfer-voucher/${record.t_id}`
            )
          }
        >
          View
        </Button>
      </>
    );
  },
}

  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "vch_no" },
    { title: "Date", dataIndex: "date", render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-", },
    { title: "To Branch", dataIndex: "branch" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    { title: "Qty", dataIndex: "qty" },
    { title: "Rate", dataIndex: "rate" },
    { title: "Total", dataIndex: "total" },
    !printMode && {
      title: "Actions",
      render: (_, record) => (
        <>
          {record.status === "Pending" && (
            <>
              <Button
                type="link"
                onClick={() => navigate(`/inventory/transfer-entry/edit/${record.t_id}`)}
                icon={<EditOutlined />}
              >               
              </Button>

              <Button
                type="link"
                danger
                onClick={() => handleDelete(record.t_id)}
                icon={<DeleteOutlined />}
              >               
              </Button>
            </>
          )}

          <Button
            type="link"
            onClick={() => navigate(`/inventory/quick-transfer-voucher/${record.t_id}`)}
          >
            View
          </Button>
        </>
      )
    }

  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;
  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.qty += Number(row.masterQty || 0);
        return acc;
      },
      { total: 0, qty: 0 }
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.transport_cost += Number(row.transport_cost || 0);
        return acc;
      },
      { total: 0, transport_cost: 0 }
    );
  }

  const expandable =
    filters.recordType === "with item"
      ? {
          expandedRowRender: (record) => (
            <div style={{ fontSize: "12px", color: "#555" }}>
              {record.unitDetails}
            </div>
          ),
          rowExpandable: (record) => !!record.unitDetails,
        }
      : undefined;

  return (
    <Table
      rowKey="id"
      columns={
        filters.recordType === "with item" ? withItemColumns : summaryColumns
      }
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}

      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={9}>
                <strong>Grand Total :</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>{totals.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={8}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                {totals.transport_cost.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                {totals.total.toFixed(2)}
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={7}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default TransferRecordTable;
